// Renderer performance monitoring — reports the dashboard's long animation frames.
// Observes long animation frames (W3C Long Animation Frames API — frames whose
// update took over 50 ms) and reports the worst to the server, which writes them
// to the structured JSONL log
// (logs_search ctx=renderer q="Long animation frame"). Never reported as an
// error, so jank stays out of the crash counter.
//
// Long animation frames rather than long tasks: a long-task entry says only that
// the thread was busy ("self"), while a frame entry names the scripts behind it
// (entry point, function, bundle position) and splits script from render and
// style/layout time — what finding the cause needs. Chromium-only (Electron and
// the Android companion); elsewhere the monitor is a silent no-op.
// https://developer.chrome.com/docs/web-platform/long-animation-frames
//
// Each report identifies its frame by pageId (random per page load) plus the
// frame's own startMs, so a report delivered twice can be told from two
// documents reporting, and carries the route shown when the frame ended — not
// when the report was sent, which can be later.
//
// Guardrails so the monitor can't become the problem it measures: only frames
// over REPORT_THRESHOLD_MS are reported, at most MAX_REPORTS_PER_WINDOW per
// window (the next report counts what was dropped), and every field is bounded.
import { RENDERER_PERF_LIMITS, type RendererPerfReport } from '@onestone/shared/schemas';
import { apiFetch } from '@/lib/api';

const REPORT_THRESHOLD_MS = 100;
const WINDOW_MS = 60_000;
const MAX_REPORTS_PER_WINDOW = 10;
const ROUTE_HISTORY_MAX = 50;

/** The script attribution fields read from a long animation frame entry. */
export interface FrameScript {
  duration: number;
  forcedStyleAndLayoutDuration: number;
  invoker: string;
  invokerType: string;
  sourceURL: string;
  sourceFunctionName: string;
  sourceCharPosition: number;
}

/** The fields read from a `long-animation-frame` entry (PerformanceLongAnimationFrameTiming). */
export interface LongAnimationFrame {
  startTime: number;
  duration: number;
  blockingDuration: number;
  renderStart: number;
  styleAndLayoutStart: number;
  scripts: readonly FrameScript[];
}

function randomId(): string {
  // getRandomValues, not randomUUID: randomUUID needs a secure context, and the
  // packaged desktop app loads from file://.
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

const pageId = randomId();
const routes: Array<{ at: number; route: string }> = [];
let observer: PerformanceObserver | null = null;
let windowStart = 0;
let reportsInWindow = 0;
let dropped = 0;

/**
 * Record that `route` is now shown. Called from the router during commit
 * (useLayoutEffect), so the time falls inside the frame that renders the route —
 * that frame then ends on the new route.
 */
export function noteRoute(route: string, at: number = performance.now()): void {
  routes.push({ at, route });
  if (routes.length > ROUTE_HISTORY_MAX) routes.shift();
}

/** The route shown at `time`; frames before the first route belong to the first. */
export function routeAt(time: number): string | undefined {
  for (let i = routes.length - 1; i >= 0; i--) {
    const entry = routes[i];
    if (entry && entry.at <= time) return entry.route;
  }
  return routes[0]?.route;
}

/** The bundle file name only — a full URL on the phone carries the relay device path. */
function fileName(url: string): string {
  return (url.split(/[?#]/, 1)[0] ?? '').split('/').pop() ?? '';
}

const ms = (value: number): number => Math.max(0, Math.round(value));

/** Build the report for one frame: timings, the longest scripts first, all bounded. */
export function toReport(
  frame: LongAnimationFrame,
  context: { pageId: string; dropped: number; route?: string | undefined; viewport?: string | undefined },
): RendererPerfReport {
  const end = frame.startTime + frame.duration;
  const scripts = [...frame.scripts]
    .sort((a, b) => b.duration - a.duration)
    .slice(0, RENDERER_PERF_LIMITS.scripts)
    .map((s) => ({
      durationMs: ms(s.duration),
      forcedLayoutMs: ms(s.forcedStyleAndLayoutDuration),
      invoker: s.invoker.slice(0, RENDERER_PERF_LIMITS.invoker),
      invokerType: s.invokerType.slice(0, RENDERER_PERF_LIMITS.invokerType),
      source: `${fileName(s.sourceURL)}:${s.sourceFunctionName}@${s.sourceCharPosition}`.slice(
        0,
        RENDERER_PERF_LIMITS.source,
      ),
    }));
  return {
    durationMs: ms(frame.duration),
    blockingMs: ms(frame.blockingDuration),
    renderMs: frame.renderStart ? ms(end - frame.renderStart) : 0,
    styleLayoutMs: frame.styleAndLayoutStart ? ms(end - frame.styleAndLayoutStart) : 0,
    startMs: ms(frame.startTime),
    pageId: context.pageId,
    ...(context.route !== undefined ? { route: context.route.slice(0, RENDERER_PERF_LIMITS.route) } : {}),
    ...(context.viewport !== undefined ? { viewport: context.viewport } : {}),
    dropped: context.dropped,
    scripts,
  };
}

function allowReport(now: number): boolean {
  if (now - windowStart > WINDOW_MS) {
    windowStart = now;
    reportsInWindow = 0;
  }
  if (reportsInWindow >= MAX_REPORTS_PER_WINDOW) return false;
  reportsInWindow++;
  return true;
}

function send(report: RendererPerfReport): void {
  void apiFetch('/api/diagnostics/renderer-perf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report),
  }).catch(() => {
    // Best-effort — if the local server is down the sample is dropped.
  });
}

/** Report the frames of one observer callback that pass the threshold and the rate limit. */
export function handleFrames(frames: readonly LongAnimationFrame[], now: number = Date.now()): void {
  for (const frame of frames) {
    if (frame.duration < REPORT_THRESHOLD_MS) continue;
    if (!allowReport(now)) {
      dropped++;
      continue;
    }
    send(
      toReport(frame, {
        pageId,
        dropped,
        route: routeAt(frame.startTime + frame.duration),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      }),
    );
    dropped = 0;
  }
}

/**
 * Start observing long animation frames. Idempotent — a second call is a no-op.
 * Safe to call unconditionally at boot; does nothing where the entry type is
 * unsupported.
 */
export function startPerformanceObserver(): void {
  if (observer) return;
  if (typeof PerformanceObserver === 'undefined') return;
  if (!PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')) return;
  observer = new PerformanceObserver((list) => {
    handleFrames(list.getEntries() as unknown as LongAnimationFrame[]);
  });
  // buffered: frames from before this call (the app's first render) are delivered too.
  observer.observe({ type: 'long-animation-frame', buffered: true });
}
